import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Panel } from '../../components/Panel'
import { Metric } from '../../components/Metric'
import { Sparkline } from '../../components/charts/Sparkline'
import { visitorsApi, type AutoRenewFilter } from '../../api/visitors'
import { fmtDate, fmtInt, PUBLICATION_NAMES } from '../../lib/format'
import { errorDetail } from '../../lib/visitorsFormat'
import { reasonLabel, scoreTone } from '../../lib/visitorsEngagement'
import { useVisitorsRange } from '../../lib/useVisitorsRange'
import { QueryProblem, Skeleton, VisitorsFrame, linkCls, th } from './VisitorsFrame'
import { btn, COLLECTING_TEXT } from './engagementBits'
import { ScoreDayNote } from './ScoreDayNote'

const PAGE = 50
const WITHIN = [30, 60, 90]
const RENEW: { v: AutoRenewFilter; label: string }[] = [
  { v: 'any', label: 'Any' }, { v: 'on', label: 'Auto-renew on' }, { v: 'off', label: 'Auto-renew off' }, { v: 'expired_card', label: 'Expired card' },
]
const TONE = { red: 'text-radar-red', amber: 'text-amber-glow', green: 'text-radar-green' } as const

export function VisitorsAtRisk() {
  const { pub = '' } = useParams()
  const { search } = useVisitorsRange()
  const okPub = Object.hasOwn(PUBLICATION_NAMES, pub)
  const [within, setWithin] = useState(30)
  const [autoRenew, setAutoRenew] = useState<AutoRenewFilter>('any')
  const [offset, setOffset] = useState(0)
  const q = useQuery({
    queryKey: ['visitors-at-risk', pub, within, autoRenew, offset],
    queryFn: () => visitorsApi.atRisk(pub, within, autoRenew, PAGE, offset),
    enabled: okPub,
    placeholderData: (prev) => prev,
    retry: false,
  })
  const save = useMutation({
    mutationFn: () => visitorsApi.saveAtRiskSegment({
      publication: pub, within, auto_renew: autoRenew,
      name: `At risk · ${PUBLICATION_NAMES[pub] ?? pub} · ${within}d · ${new Date().toISOString().slice(0, 10)}`,
    }),
  })
  const base = `/visitors/${pub}`
  const d = q.data
  const pick = (fn: () => void) => { fn(); setOffset(0); save.reset() }

  return (
    <VisitorsFrame pub={pub} valid={okPub} title="At risk" crumbs={[{ label: 'At risk' }]}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="label">Renews within</span>
        {WITHIN.map((n) => <button key={n} type="button" aria-pressed={within === n} className={btn(within === n)} onClick={() => pick(() => setWithin(n))}>{n} days</button>)}
        <span className="label ml-4">Auto-renew</span>
        {RENEW.map((o) => <button key={o.v} type="button" aria-pressed={autoRenew === o.v} className={btn(autoRenew === o.v)} onClick={() => pick(() => setAutoRenew(o.v))}>{o.label}</button>)}
      </div>

      {q.isError ? (
        <QueryProblem error={q.error} pub={pub} />
      ) : !d ? (
        <Skeleton h="h-64" />
      ) : d.collecting || d.score_day === null ? (
        <Panel accent="amber">
          <p className="text-paper">{d.collecting ? COLLECTING_TEXT : 'Collecting — no scores have been computed yet'}</p>
          <p className="font-mono text-[11px] text-paper-faint mt-1">This is not an empty list: the at-risk list stays off until there is enough reading data.</p>
        </Panel>
      ) : (
        <>
          <ScoreDayNote scoreDay={d.score_day} />
          <Panel accent="amber">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Metric size="sm" label="Auto-renew on" value={d.counts.auto_renew_on} />
              <Metric size="sm" label="Auto-renew off" value={d.counts.auto_renew_off} />
              <Metric size="sm" tone={d.counts.expired_card > 0 ? 'red' : 'paper'} label="Card expires before renewal" value={d.counts.expired_card} />
              <Metric size="sm" label="Card unknown" value={d.counts.card_unknown} sub="No card expiry on file (SimpleCirc-billed or eCheck)" />
            </div>
          </Panel>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className={btn(false)} disabled={save.isPending || d.total === 0} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save as segment'}
            </button>
            {save.data && (
              <span className="font-mono text-[12px] text-radar-green">
                Saved as segment {save.data.slug} — {fmtInt(save.data.members)} members{' '}
                <Link to="/segments" className={linkCls}>Open Segments</Link>
              </span>
            )}
            {save.isError && <span className="font-mono text-[12px] text-radar-red">{errorDetail(save.error)}</span>}
          </div>
          <Panel accent="red" bodyClassName="p-0">
            <div className="overflow-x-auto">
              <table className="w-full font-mono text-[12px] text-paper">
                <thead>
                  <tr className="border-b border-ink-500">
                    {['Member', 'Renews', 'Plan', 'Auto-renew', 'Last read', 'Reading days (90d)', 'Emails opened (90d)', 'Score', 'Trend', 'Why'].map((h) => <th key={h} className={th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((r) => (
                    <tr key={r.subscriber_id} className="border-b border-ink-500/50">
                      <td className="px-3 py-2"><Link to={`${base}/members/${r.subscriber_id}?${search}`} className={linkCls}>#{r.subscriber_id}</Link></td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.renews_on)}</td>
                      <td className="px-3 py-2">{r.plan ?? '—'}</td>
                      <td className="px-3 py-2">
                        {r.auto_renew ? 'On' : 'Off'}
                        {r.auto_renew && (r.expired_card === true
                          ? <span className="text-radar-red"> · card expired</span>
                          : r.expired_card === null
                            ? <span className="text-paper-faint" title="No card expiry on file (SimpleCirc-billed or eCheck)"> · card unknown</span>
                            : null)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.last_read_at ? fmtDate(r.last_read_at) : 'never'}</td>
                      <td className="px-3 py-2 text-right">{fmtInt(r.reads_90d)}</td>
                      <td className="px-3 py-2 text-right">{fmtInt(r.email_opens_90d)}</td>
                      <td className={`px-3 py-2 ${r.score === null ? 'text-paper-faint' : TONE[scoreTone(r.score)]}`}>{r.score ?? '—'}</td>
                      <td className="px-3 py-2"><Sparkline values={r.trend.filter((x): x is number => x !== null)} ariaLabel="Score trend, last 8 weeks" /></td>
                      <td className="px-3 py-2">{reasonLabel(r.reason)}</td>
                    </tr>
                  ))}
                  {d.items.length === 0 && <tr><td colSpan={10} className="px-3 py-4 text-paper-faint">No at-risk members match these filters</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 px-3 py-3 font-mono text-[12px] text-paper-dim">
              <span>{fmtInt(d.total > 0 ? offset + 1 : 0)}–{fmtInt(Math.min(offset + PAGE, d.total))} of {fmtInt(d.total)}</span>
              <span className="flex gap-2">
                <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} className="min-h-[32px] px-3 border border-ink-500 hover:bg-ink-700 disabled:opacity-40">Prev</button>
                <button type="button" disabled={offset + PAGE >= d.total} onClick={() => setOffset(offset + PAGE)} className="min-h-[32px] px-3 border border-ink-500 hover:bg-ink-700 disabled:opacity-40">Next</button>
              </span>
            </div>
          </Panel>
        </>
      )}
    </VisitorsFrame>
  )
}
